import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://tbkmeet.com.tr';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token yenileme durumu
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

function clearStoredAuth() {
  localStorage.removeItem('tbkmeet_token');
  localStorage.removeItem('tbkmeet_refresh_token');
  localStorage.removeItem('tbkmeet_user');
}

/** Toplantı sayfasındayken zorla login'e atmak WebRTC oturumunu koparır. */
function redirectToLoginIfSafe() {
  if (window.location.pathname.startsWith('/meeting/')) return;
  window.location.href = '/login';
}

// İstek öncesi token ekle
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tbkmeet_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Yanıt interceptor:
// - 401: oturum süresi doldu → refresh + retry
// - 403: bazı servisler süresi dolmuş JWT'ye de 403 döner → bir kez refresh dene;
//   refresh sonrası hâlâ 403 ise gerçek yetki hatasıdır, aynen iletilir.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status === 403 &&
      originalRequest &&
      !originalRequest._retry &&
      localStorage.getItem('tbkmeet_refresh_token')
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('tbkmeet_refresh_token');
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refreshToken,
        });
        const { token: newToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('tbkmeet_token', newToken);
        if (newRefreshToken) {
          localStorage.setItem('tbkmeet_refresh_token', newRefreshToken);
        }
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        // Refresh olmadı → orijinal 403 aynen dönsün (oturumu bozma)
        return Promise.reject(error);
      }
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('tbkmeet_refresh_token');
        
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refreshToken,
        });

        const { token: newToken, refreshToken: newRefreshToken } = response.data.data;

        localStorage.setItem('tbkmeet_token', newToken);
        if (newRefreshToken) {
          localStorage.setItem('tbkmeet_refresh_token', newRefreshToken);
        }

        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearStoredAuth();
        redirectToLoginIfSafe();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;